import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { CloseIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { Button } from '@/components/ui/Button/Button';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import {
  mergeHistoryPage,
  sessionHistoryQueryKey,
  useSessionChangeHistory,
} from './sessionChangeHistoryQuery';
// We reuse the Project history UI components
import { HistoryGroupRow } from '../project-change-history/HistoryGroupRow';
import { groupHistoryByChangeSet } from '../project-change-history/groupHistoryByChangeSet';
import type { SessionChangeHistoryPage } from './types';
import '../project-change-history/ProjectChangeHistoryPopover.css';

/** `below-cell` — под ячейкой; `beside-menu` — слева от кнопки/меню, по центру высоты якоря */
export type SessionChangeHistoryAnchorPlacement = 'below-cell' | 'beside-menu';

export interface SessionChangeHistoryPopoverProps {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  /** Откуда открыли попап — влияет на позиционирование относительно якоря */
  anchorPlacement?: SessionChangeHistoryAnchorPlacement;
  sessionId: string;
  title?: string;
}

export function SessionChangeHistoryPopover({
  open,
  onClose,
  anchorEl,
  anchorPlacement = 'below-cell',
  sessionId,
  title,
}: SessionChangeHistoryPopoverProps) {
  const queryClient = useQueryClient();
  const popoverRef = useRef<HTMLDivElement>(null);
  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(open, onClose, true);
  
  const { data, isPending, isError, error } = useSessionChangeHistory(sessionId, {
    enabled: shouldRender,
  });
  
  const displayGroups = useMemo(
    () => groupHistoryByChangeSet(data?.items ?? []),
    [data?.items]
  );

  const [loadingMore, setLoadingMore] = useState(false);

  const besideMenu = anchorPlacement === 'beside-menu';

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionX: 'left',
    preferredPositionY: 'bottom',
    horizontalAlign: 'center',
    verticalAlign: besideMenu ? 'center' : 'start',
    priorityAxis: besideMenu ? 'x' : 'y',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1100,
    clampOnScrollFollow: !besideMenu,
    onAnchorDetached: requestClose,
  });

  const anchorExcludeRef = useRef<HTMLElement | null>(null);
  anchorExcludeRef.current = anchorEl;

  useClickOutside(popoverRef, requestClose, {
    excludeRefs: [anchorExcludeRef as React.RefObject<HTMLElement | null>],
    enabled: shouldRender,
  });

  const heading = title ?? 'Изменения сессии';

  const loadMore = useCallback(async () => {
    if (!data?.next_cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await api.sessions.getChanges(sessionId, {
        before_cursor: data.next_cursor,
      });
      const key = sessionHistoryQueryKey(sessionId);
      queryClient.setQueryData<SessionChangeHistoryPage>(key, (prev) =>
        mergeHistoryPage(prev, next)
      );
    } finally {
      setLoadingMore(false);
    }
  }, [data?.next_cursor, loadingMore, sessionId, queryClient]);

  if (!shouldRender) return null;

  return createPortal(
    <div ref={popoverRef} style={{ position: 'fixed', left: '-9999px', top: '-9999px' }}>
      <OverlayIntrinsicSize>
        <div
          // Reuse project CSS classes to ensure identical UI
          className={`project-change-history-popover ${contentClasses}`}
          role="dialog"
          aria-label={heading}
        >
          <div className="project-change-history-popover__header">
            <h2 className="project-change-history-popover__title">{heading}</h2>
            <IconButton
              type="button"
              variant="danger"
              size="m"
              iconSize={20}
              className="project-change-history-popover__close"
              aria-label="Закрыть"
              onClick={requestClose}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className="project-change-history-popover__body">
            <OverlayScrollArea className="project-change-history-popover__scroll">
              <div className="project-change-history-popover__scroll-pad">
                {isPending && (
                  <div
                    className="project-change-history-popover__loading"
                    aria-busy="true"
                    aria-live="polite"
                  >
                    <span
                      className="project-change-history-popover__loading-spinner"
                      aria-label="Загрузка истории"
                    />
                  </div>
                )}
                {isError && (
                  <p className="project-change-history-popover__empty">
                    {(error as Error)?.message ?? 'Не удалось загрузить историю'}
                  </p>
                )}
                {!isPending && !isError && (
                  <ul className="project-change-history-popover__list" role="list">
                    {displayGroups.length === 0 ? (
                      <li className="project-change-history-popover__empty">
                        <i
                          className="fa-solid fa-hourglass-half project-change-history-popover__empty-icon"
                          aria-hidden
                        />
                        Изменений пока нет
                      </li>
                    ) : (
                      displayGroups.map((group) => (
                        <HistoryGroupRow key={group.group_key} group={group} />
                      ))
                    )}
                  </ul>
                )}
              </div>
            </OverlayScrollArea>
            {!isPending && !isError && data?.next_cursor && (
              <div className="project-change-history-popover__footer">
                <Button
                  type="button"
                  variant="secondary"
                  size="s"
                  className="project-change-history-popover__load-more"
                  disabled={loadingMore}
                  onClick={() => void loadMore()}
                >
                  {loadingMore ? (
                    'Загрузка…'
                  ) : (
                    <>
                      <i className="fa-solid fa-chevron-up" aria-hidden /> Показать раньше
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </OverlayIntrinsicSize>
    </div>,
    document.body
  );
}
